<template>
  <div 
    v-if="isShowBridgeSelector" 
    class="bridge-tree-selector"
    @mouseenter="showPanel"
    @mouseleave="hidePanel"
    ref="trigger"
  >
    <!-- 桥梁选择标签 -->
    <div class="selector-label">
      <i class="el-icon-s-operation selector-icon"></i>
      <span class="selector-text">{{ displayText }}</span>
      <i class="el-icon-arrow-down selector-arrow" :class="{ 'rotated': isVisible }"></i>
    </div>
  </div>
</template>

<script>
import { getUserBridges, getAllBridges } from '@/api/bridge'
import { getUserInfo } from '@/api/auth'

export default {
  name: 'BridgeTreeSelector',
  props: {
    isShowBridgeSelector: {
      type: Boolean,
      default: true
    },
    selectedBridgeId: {
      type: [Number, String],
      default: ''
    },
    bridgeOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      panelElement: null,
      isVisible: false,
      hideTimer: null,
      panelCached: false, // 标记面板是否已缓存
      
      // 树形数据
      treeData: [],
      checkedBridges: [], // 选中的桥梁ID列表
      selectedBridges: [], // 选中的桥梁对象列表
      allBridgeIds: [], // 所有桥梁ID列表
      
      // 树形配置
      defaultProps: {
        children: 'children',
        label: 'name',
        disabled: 'disabled'
      },
      
      // 搜索关键词
      searchKeyword: '',
      
      // 加载状态
      loading: false
    }
  },
  computed: {
    displayText() {
      if (this.selectedBridges.length === 0) {
        return '选择桥梁'
      } else if (this.selectedBridges.length === 1) {
        return this.selectedBridges[0].name
      } else {
        // 多选时显示主桥梁名称
        const primaryBridge = this.selectedBridges[0]
        return `${primaryBridge.name}`
      }
    }
  },
  watch: {
    bridgeOptions: {
      handler(newOptions) {
        console.log('【BridgeTreeSelector】bridgeOptions变化:', newOptions.length, 'selectedBridgeId:', this.selectedBridgeId)
        if (newOptions && newOptions.length > 0) {
          this.initTreeData(newOptions)
          
          // 如果有selectedBridgeId但还没有选择，则初始化默认选择
          if (this.selectedBridgeId && this.checkedBridges.length === 0) {
            this.initDefaultSelection(this.selectedBridgeId)
          }
          
          // 如果面板已缓存，需要重新渲染内容
          if (this.panelCached && this.panelElement) {
            console.log('【BridgeTreeSelector】桥梁数据变化，重新渲染缓存面板')
            this.renderTreeContent()
          }
        }
      },
      immediate: true,
      deep: true
    },
    selectedBridgeId: {
      handler(newId) {
        console.log('【BridgeTreeSelector】selectedBridgeId变化:', newId, 'bridgeOptions length:', this.bridgeOptions.length)
        if (newId && this.bridgeOptions.length > 0) {
          this.initDefaultSelection(newId)
        }
      },
      immediate: true
    },
    // 监听路由变化
    '$route'(newRoute, oldRoute) {
      // 隐藏面板（如果正在显示）
      if (this.isVisible) {
        this.hideCachedPanel()
        this.isVisible = false
      }
    }
  },
  methods: {
    /**
     * 初始化树形数据
     */
    initTreeData(bridges) {
      this.allBridgeIds = bridges.map(bridge => bridge.id.toString())
      
      this.treeData = [
        {
          id: 'root',
          name: '全部桥梁',
          children: bridges.map(bridge => ({
            id: bridge.id.toString(),
            name: bridge.name,
            originalData: bridge
          }))
        }
      ]
    },
    
    /**
     * 初始化默认选择
     */
    initDefaultSelection(bridgeId) {
      // 首先尝试从localStorage恢复多桥梁选择状态
      const restored = this.restoreMultiBridgeSelection()
      
      if (restored) {
        console.log('【BridgeTreeSelector】成功恢复多桥梁选择状态')
        // 更新UI
        this.$nextTick(() => {
          this.updateTreeUI()
          this.emitChange()
        })
        return
      }
      
      // 如果没有已选择的桥梁，则选择传入的桥梁ID
      if (this.checkedBridges.length === 0 && bridgeId) {
        this.checkedBridges = [bridgeId.toString()]
        this.updateSelectedBridges()
        
        // 初始化时也要发出选择变更事件
        console.log('【BridgeTreeSelector】初始化默认选择:', bridgeId)
        this.$nextTick(() => {
          this.emitChange()
        })
      }
    },
    
    /**
     * 显示面板
     */
    showPanel() {
      console.log('【BridgeTreeSelector】触发显示面板')
      
      // 清除隐藏定时器
      if (this.hideTimer) {
        console.log('【BridgeTreeSelector】清除隐藏定时器')
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
      
      if (!this.isVisible) {
        if (this.panelElement && this.panelCached) {
          // 使用缓存的面板
          console.log('【BridgeTreeSelector】使用缓存面板')
          this.showCachedPanel()
        } else {
          // 创建新面板
          console.log('【BridgeTreeSelector】创建新面板')
          this.createPanel()
        }
        this.isVisible = true
      }
    },
    
    /**
     * 隐藏面板
     */
    hidePanel() {
      // 清除之前的定时器，避免重复设置
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
      
      // 延迟隐藏，给用户时间移动到面板上
      this.hideTimer = setTimeout(() => {
        console.log('【BridgeTreeSelector】执行面板隐藏')
        this.hideCachedPanel()
        this.isVisible = false
        this.hideTimer = null
      }, 150) // 增加延迟时间，提高容错性
    },
    
    /**
     * 创建悬浮面板
     */
    createPanel() {
      // 检查是否有桥梁数据，没有则不创建面板
      if (!this.bridgeOptions || this.bridgeOptions.length === 0) {
        console.warn('【BridgeTreeSelector】没有桥梁数据，无法创建面板')
        return
      }
      
      // 确保树形数据已初始化
      if (!this.treeData || this.treeData.length === 0) {
        console.log('【BridgeTreeSelector】重新初始化树形数据')
        this.initTreeData(this.bridgeOptions)
      }
      
      // 创建面板元素
      const panel = document.createElement('div')
      panel.className = 'bridge-tree-floating-panel'
      
      // 设置面板内容
      panel.innerHTML = this.getPanelHTML()
      
      // 添加鼠标事件 - 使用更稳定的事件处理
      panel.addEventListener('mouseenter', this.handlePanelMouseEnter)
      panel.addEventListener('mouseleave', this.handlePanelMouseLeave)
      
      // 设置样式但不显示
      panel.style.position = 'fixed'
      panel.style.zIndex = '99999'
      panel.style.opacity = '0'
      panel.style.transform = 'translateY(-8px)'
      panel.style.pointerEvents = 'none'
      
      // 添加到body但隐藏
      document.body.appendChild(panel)
      this.panelElement = panel
      this.panelCached = true
      
      console.log('【BridgeTreeSelector】面板已创建并缓存')
      
      // 渲染树形选择器内容
      this.$nextTick(() => {
        if (this.panelElement && this.panelElement === panel) {
          this.renderTreeContent()
          this.bindSearchEvents()
          // 首次创建完成后立即显示
          this.showCachedPanel()
        }
      })
    },
    
    /**
     * 处理面板鼠标进入事件
     */
    handlePanelMouseEnter() {
      console.log('【BridgeTreeSelector】鼠标进入面板，清除隐藏定时器')
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },
    
    /**
     * 处理面板鼠标离开事件
     */
    handlePanelMouseLeave(e) {
      // 检查鼠标是否真的离开了面板区域
      if (this.panelElement && e.relatedTarget && this.panelElement.contains(e.relatedTarget)) {
        console.log('【BridgeTreeSelector】鼠标仍在面板内，不隐藏')
        return
      }
      
      console.log('【BridgeTreeSelector】鼠标离开面板，启动隐藏定时器')
      this.hidePanel()
    },
    
    /**
     * 显示缓存的面板
     */
    showCachedPanel() {
      if (!this.panelElement) return
      
      // 计算位置
      const rect = this.$refs.trigger.getBoundingClientRect()
      this.panelElement.style.top = `${rect.bottom + 8}px`
      this.panelElement.style.left = `${rect.left}px`
      
      // 显示面板
      this.panelElement.style.pointerEvents = 'auto'
      this.panelElement.style.opacity = '1'
      this.panelElement.style.transform = 'translateY(0)'
      
      // 更新面板内容（选择状态可能已改变）
      this.updatePanelContent()
      
      console.log('【BridgeTreeSelector】显示缓存面板')
    },
    
    /**
     * 隐藏缓存的面板
     */
    hideCachedPanel() {
      if (!this.panelElement) return
      
      // 隐藏面板但不销毁
      this.panelElement.style.opacity = '0'
      this.panelElement.style.transform = 'translateY(-8px)'
      this.panelElement.style.pointerEvents = 'none'
      
      console.log('【BridgeTreeSelector】隐藏缓存面板')
    },
    
    /**
     * 更新面板内容
     */
    updatePanelContent() {
      if (!this.panelElement) return
      
      // 更新头部选择计数
      const selectionCount = this.panelElement.querySelector('.selection-count')
      if (selectionCount) {
        selectionCount.textContent = `已选择 ${this.selectedBridges.length} 个桥梁`
      }
      
      // 更新树形选择状态
      this.updateTreeUI()
    },
    
    /**
     * 销毁面板（仅在组件销毁时调用）
     */
    destroyPanel() {
      if (this.panelElement) {
        console.log('【BridgeTreeSelector】彻底销毁面板')
        
        // 清理面板级别的事件监听器
        this.panelElement.removeEventListener('mouseenter', this.handlePanelMouseEnter)
        this.panelElement.removeEventListener('mouseleave', this.handlePanelMouseLeave)
        
        // 立即清理子元素事件监听器
        const inputs = this.panelElement.querySelectorAll('.tree-checkbox')
        inputs.forEach(input => {
          input.removeEventListener('change', this.handleInputChange)
        })
        
        // 清理定位图标事件监听器
        const primaryIcons = this.panelElement.querySelectorAll('.primary-bridge-icon')
        primaryIcons.forEach(icon => {
          icon.removeEventListener('click', this.handlePrimaryBridgeClick)
        })
        
        // 清理搜索框事件监听器
        const searchInput = this.panelElement.querySelector('#bridge-search-input')
        if (searchInput) {
          searchInput.removeEventListener('input', this.handleSearchInput)
          searchInput.removeEventListener('click', this.handleSearchClick)
        }
        
        const clearBtn = this.panelElement.querySelector('#clear-search-btn')
        if (clearBtn) {
          clearBtn.removeEventListener('click', this.handleClearSearch)
        }
        
        // 立即移除DOM元素
        if (this.panelElement.parentNode) {
          this.panelElement.parentNode.removeChild(this.panelElement)
        }
        
        this.panelElement = null
        this.panelCached = false
      }
    },
    
    /**
     * 获取面板HTML内容
     */
    getPanelHTML() {
      return `
        <div class="panel-header">
          <span class="panel-title">桥梁选择</span>
          <span class="selection-count">已选择 ${this.selectedBridges.length} 个桥梁</span>
        </div>
        
        <div class="search-container">
          <i class="el-icon-search search-icon"></i>
          <input 
            type="text" 
            class="search-input" 
            placeholder="搜索桥梁名称..." 
            id="bridge-search-input"
          />
          <i class="el-icon-circle-close clear-icon" id="clear-search-btn" style="display: none;"></i>
        </div>
        
        <div class="panel-content">
          <div class="tree-container" id="bridge-tree-container">
            <!-- 树形控件将在这里渲染 -->
          </div>
        </div>
      `
    },
    
    /**
     * 渲染树形内容
     */
    renderTreeContent() {
      // 多重安全检查
      if (!this.panelElement) {
        console.warn('【BridgeTreeSelector】面板不存在，跳过渲染')
        return
      }
      
      const treeContainer = document.getElementById('bridge-tree-container')
      if (!treeContainer) {
        console.warn('【BridgeTreeSelector】树形容器不存在，跳过渲染')
        return
      }
      
      // 检查树形容器是否在当前面板中
      if (!this.panelElement.contains(treeContainer)) {
        console.warn('【BridgeTreeSelector】树形容器不在当前面板中，跳过渲染')
        return
      }
      
      console.log('【BridgeTreeSelector】渲染树形内容:', {
        bridgeOptionsLength: this.bridgeOptions.length,
        treeDataLength: this.treeData.length,
        checkedBridges: this.checkedBridges,
        searchKeyword: this.searchKeyword
      })
      
      // 如果没有桥梁数据，显示提示信息
      if (!this.bridgeOptions || this.bridgeOptions.length === 0) {
        treeContainer.innerHTML = '<div class="no-data-tip">暂无桥梁数据</div>'
        return
      }
      
      // 确保树形数据存在
      if (!this.treeData || this.treeData.length === 0) {
        console.log('【BridgeTreeSelector】树形数据不存在，重新初始化')
        this.initTreeData(this.bridgeOptions)
      }
      
      // 过滤桥梁数据
      let filteredBridges = this.treeData.length > 0 && this.treeData[0].children 
        ? this.treeData[0].children 
        : []
      
      if (this.searchKeyword.trim()) {
        const keyword = this.searchKeyword.trim().toLowerCase()
        filteredBridges = filteredBridges.filter(bridge => 
          bridge.name.toLowerCase().includes(keyword)
        )
      }
      
      // 创建简单的树形HTML结构
      let treeHTML = '<div class="simple-tree">'
      
      // 只有在没有搜索时才显示根节点（全部桥梁）
      if (!this.searchKeyword.trim()) {
        const allChecked = this.checkedBridges.length === this.allBridgeIds.length
        treeHTML += `
          <div class="tree-node root-node">
            <label class="tree-node-label">
              <input type="checkbox" class="tree-checkbox" data-id="root" ${allChecked ? 'checked' : ''}/>
              <span class="tree-node-text">全部桥梁</span>
            </label>
          </div>
        `
      }
      
      // 渲染子节点
      if (filteredBridges.length > 0) {
        filteredBridges.forEach((bridge, index) => {
          const isChecked = this.checkedBridges.includes(bridge.id)
          const isPrimary = this.checkedBridges.length > 0 && this.checkedBridges[0] === bridge.id
          
          treeHTML += `
            <div class="tree-node child-node">
              <label class="tree-node-label">
                <input type="checkbox" class="tree-checkbox" data-id="${bridge.id}" ${isChecked ? 'checked' : ''}/>
                <span class="tree-node-text">${bridge.name}</span>
                ${isChecked ? `
                  <span class="primary-bridge-icon ${isPrimary ? 'is-primary' : ''}" 
                        data-bridge-id="${bridge.id}" 
                        title="${isPrimary ? '当前定位桥梁' : '设为定位桥梁'}">
                    <i class="el-icon-location"></i>
                  </span>
                ` : ''}
              </label>
            </div>
          `
        })
      } else if (this.searchKeyword.trim()) {
        // 搜索无结果提示
        treeHTML += `
          <div class="no-data-tip">
            <i class="el-icon-search" style="font-size: 32px; margin-bottom: 8px;"></i>
            <div>未找到匹配的桥梁</div>
          </div>
        `
      }
      
      treeHTML += '</div>'
      
      // 设置内容
      treeContainer.innerHTML = treeHTML
      
      // 绑定复选框事件
      const inputs = treeContainer.querySelectorAll('.tree-checkbox')
      inputs.forEach(input => {
        input.addEventListener('change', this.handleInputChange)
      })
      
      // 绑定定位图标点击事件
      const primaryIcons = treeContainer.querySelectorAll('.primary-bridge-icon')
      primaryIcons.forEach(icon => {
        icon.addEventListener('click', this.handlePrimaryBridgeClick)
      })
    },
    
    /**
     * 绑定搜索框事件
     */
    bindSearchEvents() {
      if (!this.panelElement) return
      
      const searchInput = document.getElementById('bridge-search-input')
      const clearBtn = document.getElementById('clear-search-btn')
      
      if (searchInput) {
        searchInput.addEventListener('input', this.handleSearchInput)
        searchInput.addEventListener('click', this.handleSearchClick)
      }
      
      if (clearBtn) {
        clearBtn.addEventListener('click', this.handleClearSearch)
      }
    },
    
    /**
     * 处理搜索输入事件
     */
    handleSearchInput(e) {
      this.searchKeyword = e.target.value
      this.renderTreeContent()
      
      // 控制清除按钮显示
      const clearBtn = document.getElementById('clear-search-btn')
      if (clearBtn) {
        clearBtn.style.display = this.searchKeyword ? 'block' : 'none'
      }
    },
    
    /**
     * 处理搜索框点击事件
     */
    handleSearchClick(e) {
      e.stopPropagation()
    },
    
    /**
     * 处理清除搜索事件
     */
    handleClearSearch() {
      this.searchKeyword = ''
      const searchInput = document.getElementById('bridge-search-input')
      if (searchInput) {
        searchInput.value = ''
      }
      const clearBtn = document.getElementById('clear-search-btn')
      if (clearBtn) {
        clearBtn.style.display = 'none'
      }
      this.renderTreeContent()
    },
    
    /**
     * 统一的输入事件处理方法
     */
    handleInputChange(e) {
      // 检查面板是否仍然存在
      if (!this.panelElement) {
        console.warn('【BridgeTreeSelector】面板已销毁，忽略输入事件')
        return
      }
      
      const nodeId = e.target.getAttribute('data-id')
      const isChecked = e.target.checked
      
      this.handleSimpleTreeCheck(nodeId, isChecked)
    },
    
    /**
     * 处理定位桥梁图标点击
     */
    handlePrimaryBridgeClick(e) {
      e.preventDefault()
      e.stopPropagation()
      
      const bridgeId = e.currentTarget.getAttribute('data-bridge-id')
      if (!bridgeId || !this.checkedBridges.includes(bridgeId)) {
        console.warn('【BridgeTreeSelector】无效的定位桥梁ID或桥梁未选中:', bridgeId)
        return
      }
      
      // 如果已经是定位桥梁，则不需要处理
      if (this.checkedBridges[0] === bridgeId) {
        console.log('【BridgeTreeSelector】该桥梁已经是定位桥梁:', bridgeId)
        return
      }
      
      // 将选中的桥梁移到第一位（设为定位桥梁）
      const currentIndex = this.checkedBridges.indexOf(bridgeId)
      if (currentIndex > 0) {
        // 从当前位置移除
        this.checkedBridges.splice(currentIndex, 1)
        // 插入到第一位
        this.checkedBridges.unshift(bridgeId)
        
        console.log('【BridgeTreeSelector】设置定位桥梁:', bridgeId, '新顺序:', this.checkedBridges)
        
        // 更新相关状态
        this.updateSelectedBridges()
        this.updateTreeUI()
        this.updatePanelContent()
        
        // 触发选择变更事件
        this.emitChange()
      }
    },
    
    /**
     * 处理简单树形复选框变化
     */
    handleSimpleTreeCheck(nodeId, isChecked) {
      // 检查面板是否仍然存在
      if (!this.panelElement) {
        console.warn('【BridgeTreeSelector】面板已销毁，忽略复选框事件')
        return
      }
      
      if (nodeId === 'root') {
        // 点击根节点，全选或取消全选
        if (isChecked) {
          this.checkedBridges = [...this.allBridgeIds]
        } else {
          // 检查是否至少保留一个桥梁
          if (this.checkedBridges.length <= 1) {
            this.$message.warning('至少选择1个桥梁')
            // 恢复根节点复选框状态
            const rootCheckbox = this.panelElement.querySelector('[data-id="root"]')
            if (rootCheckbox) {
              rootCheckbox.checked = true
            }
            return
          }
          // 如果当前是全选状态，保留第一个桥梁
          this.checkedBridges = [this.allBridgeIds[0]]
        }
      } else {
        // 点击子节点
        if (isChecked) {
          if (!this.checkedBridges.includes(nodeId)) {
            this.checkedBridges.push(nodeId)
          }
        } else {
          // 检查是否至少保留一个桥梁
          if (this.checkedBridges.length <= 1) {
            this.$message.warning('至少选择1个桥梁')
            // 恢复该复选框状态
            const checkbox = this.panelElement.querySelector(`[data-id="${nodeId}"]`)
            if (checkbox) {
              checkbox.checked = true
            }
            return
          }
          const index = this.checkedBridges.indexOf(nodeId)
          if (index !== -1) {
            this.checkedBridges.splice(index, 1)
          }
        }
      }
      
      // 更新UI状态
      this.updateTreeUI()
      this.updateSelectedBridges()
      this.updatePanelContent() // 更新面板内容，包括主桥梁名称
      
      // 立即触发选择变更事件
      this.emitChange()
    },
    
    /**
     * 更新树形UI状态
     */
    updateTreeUI() {
      if (!this.panelElement) return
      
      const treeContainer = this.panelElement.querySelector('#bridge-tree-container')
      if (!treeContainer) return
      
      // 更新根节点状态
      const rootCheckbox = treeContainer.querySelector('[data-id="root"]')
      if (rootCheckbox) {
        rootCheckbox.checked = this.checkedBridges.length === this.allBridgeIds.length
      }
      
      // 更新子节点状态和图标
      this.allBridgeIds.forEach(bridgeId => {
        const input = treeContainer.querySelector(`[data-id="${bridgeId}"]`)
        if (input) {
          input.checked = this.checkedBridges.includes(bridgeId)
        }
        
        // 更新定位图标
        this.updateBridgeIcon(bridgeId, treeContainer)
      })
    },
    
    /**
     * 更新单个桥梁的定位图标
     */
    updateBridgeIcon(bridgeId, treeContainer) {
      const treeNode = treeContainer.querySelector(`[data-id="${bridgeId}"]`)?.closest('.tree-node')
      if (!treeNode) return
      
      const isChecked = this.checkedBridges.includes(bridgeId)
      const isPrimary = this.checkedBridges.length > 0 && this.checkedBridges[0] === bridgeId
      
      // 查找或创建图标元素
      let iconElement = treeNode.querySelector('.primary-bridge-icon')
      
      if (isChecked) {
        if (!iconElement) {
          // 创建图标元素
          const bridge = this.bridgeOptions.find(b => b.id.toString() === bridgeId)
          if (bridge) {
            iconElement = document.createElement('span')
            iconElement.className = `primary-bridge-icon ${isPrimary ? 'is-primary' : ''}`
            iconElement.setAttribute('data-bridge-id', bridgeId)
            iconElement.title = isPrimary ? '当前定位桥梁' : '设为定位桥梁'
            iconElement.innerHTML = '<i class="el-icon-location"></i>'
            iconElement.addEventListener('click', this.handlePrimaryBridgeClick)
            
            // 插入到节点标签的最后
            const label = treeNode.querySelector('.tree-node-label')
            if (label) {
              label.appendChild(iconElement)
            }
          }
        } else {
          // 更新现有图标状态
          iconElement.className = `primary-bridge-icon ${isPrimary ? 'is-primary' : ''}`
          iconElement.title = isPrimary ? '当前定位桥梁' : '设为定位桥梁'
        }
      } else {
        // 移除图标
        if (iconElement) {
          iconElement.removeEventListener('click', this.handlePrimaryBridgeClick)
          iconElement.remove()
        }
      }
    },
    
    
    /**
     * 更新选中的桥梁对象列表
     */
    updateSelectedBridges() {
      // 按照checkedBridges的顺序来排序selectedBridges，确保第一个是定位桥梁
      this.selectedBridges = this.checkedBridges
        .map(bridgeId => this.bridgeOptions.find(bridge => bridge.id.toString() === bridgeId))
        .filter(bridge => bridge !== undefined)
      
      console.log('【BridgeTreeSelector】更新选中桥梁顺序:', {
        checkedBridges: this.checkedBridges,
        selectedBridgeNames: this.selectedBridges.map(b => b.name)
      })
    },
    
    
    /**
     * 从localStorage恢复多桥梁选择状态
     */
    restoreMultiBridgeSelection() {
      try {
        const savedState = localStorage.getItem('multiBridgeSelection')
        if (savedState) {
          const multiBridgeState = JSON.parse(savedState)
          
          // 验证数据有效性
          if (multiBridgeState.selectedBridgeIds && Array.isArray(multiBridgeState.selectedBridgeIds)) {
            // 过滤出仍然存在的桥梁ID
            const validBridgeIds = multiBridgeState.selectedBridgeIds.filter(bridgeId => 
              this.bridgeOptions.some(option => option.id.toString() === bridgeId.toString())
            )
            
            if (validBridgeIds.length > 0) {
              this.checkedBridges = validBridgeIds.map(id => id.toString())
              this.updateSelectedBridges()
              
              console.log('【BridgeTreeSelector】恢复多桥梁选择状态:', {
                bridgeIds: validBridgeIds,
                bridgeNames: this.selectedBridges.map(b => b.name)
              })
              
              return true
            }
          }
        }
      } catch (error) {
        console.error('【BridgeTreeSelector】恢复多桥梁选择状态失败:', error)
      }
      return false
    },
    
    /**
     * 发出变更事件
     */
    emitChange() {
      const result = {
        selectedBridges: this.selectedBridges,
        selectedBridgeIds: this.checkedBridges,
        selectedBridgeId: this.checkedBridges.length > 0 ? this.checkedBridges[0] : null
      }
      
      console.log('【BridgeTreeSelector】桥梁选择变更:', result)
      console.log('【BridgeTreeSelector】当前checkedBridges:', this.checkedBridges)
      console.log('【BridgeTreeSelector】当前selectedBridges:', this.selectedBridges)
      this.$emit('bridge-selection-change', result)
    }
  },
  
  beforeDestroy() {
    console.log('【BridgeTreeSelector】组件销毁，清理资源')
    
    // 清除定时器
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
    
    // 销毁面板
    this.destroyPanel()
  }
}
</script>

<style scoped>
.bridge-tree-selector {
  display: inline-block;
  position: relative;
  width: 200px;
}

.selector-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  height: 30px;
  line-height: 18px;
}

.selector-label:hover {
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selector-icon {
  margin-right: 6px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  flex-shrink: 0;
}

.selector-text {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  letter-spacing: 0.5px;
}

.selector-arrow {
  margin-left: 6px;
  font-size: 12px;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.selector-arrow.rotated {
  transform: rotate(180deg);
}
</style>

<!-- 全局样式用于悬浮面板 -->
<style>
.bridge-tree-floating-panel {
  min-width: 320px;
  max-width: 400px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  backdrop-filter: blur(8px);
  opacity: 0;
  transform: translateY(-8px);
  transition: all 0.2s ease;
}

.bridge-tree-floating-panel.panel-visible {
  opacity: 1;
  transform: translateY(0);
}

.bridge-tree-floating-panel .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.bridge-tree-floating-panel .panel-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.bridge-tree-floating-panel .selection-count {
  font-size: 12px;
  color: #f2f5f8;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* 搜索容器样式 */
.bridge-tree-floating-panel .search-container {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  background: rgba(249, 250, 251, 0.5);
}

.bridge-tree-floating-panel .search-icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
}

.bridge-tree-floating-panel .search-input {
  width: 100%;
  height: 36px;
  padding: 0 32px 0 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  outline: none;
  box-sizing: border-box;
}

.bridge-tree-floating-panel .search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.bridge-tree-floating-panel .search-input::placeholder {
  color: #9ca3af;
}

.bridge-tree-floating-panel .clear-icon {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s ease;
}

.bridge-tree-floating-panel .clear-icon:hover {
  color: #374151;
  transform: translateY(-50%) scale(1.1);
}

.bridge-tree-floating-panel .panel-content {
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.bridge-tree-floating-panel .tree-container {
  min-height: 200px;
}


/* 覆盖Element UI树形控件样式 */
.bridge-tree-floating-panel .el-tree {
  background: transparent;
  color: #374151;
}

.bridge-tree-floating-panel .el-tree-node__content {
  height: 32px;
  padding: 0 8px;
  border-radius: 6px;
  margin-bottom: 2px;
  transition: all 0.2s ease;
}

.bridge-tree-floating-panel .el-tree-node__content:hover {
  background: rgba(59, 130, 246, 0.1);
}

.bridge-tree-floating-panel .el-tree-node.is-checked > .el-tree-node__content {
  background: rgba(59, 130, 246, 0.15);
  color: #1e40af;
  font-weight: 500;
}

.bridge-tree-floating-panel .el-tree-node__expand-icon {
  color: #6b7280;
}

.bridge-tree-floating-panel .el-tree-node__label {
  font-size: 13px;
  font-weight: 500;
}

.bridge-tree-floating-panel .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.bridge-tree-floating-panel .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #1e40af;
}

/* 简单树形组件样式 */
.bridge-tree-floating-panel .simple-tree {
  padding: 8px 0;
}

.bridge-tree-floating-panel .tree-node {
  padding: 4px 0;
  margin: 2px 0;
}

.bridge-tree-floating-panel .tree-node-label:hover {
  background: rgba(59, 130, 246, 0.1);
}

.bridge-tree-floating-panel .tree-checkbox {
  margin-right: 8px;
  cursor: pointer;
}

.bridge-tree-floating-panel .tree-node-text {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.bridge-tree-floating-panel .root-node .tree-node-text {
  font-weight: 600;
  color: #1e40af;
}

.bridge-tree-floating-panel .child-node {
  margin-left: 20px;
}

.bridge-tree-floating-panel .child-node .tree-node-text {
  color: #4b5563;
  flex: 1;
}

/* 定位桥梁图标样式 */
.bridge-tree-floating-panel .primary-bridge-icon {
  margin-left: 8px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  color: #d1d5db;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.bridge-tree-floating-panel .primary-bridge-icon:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  transform: scale(1.1);
}

.bridge-tree-floating-panel .primary-bridge-icon.is-primary {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.bridge-tree-floating-panel .primary-bridge-icon.is-primary:hover {
  background: rgba(245, 158, 11, 0.2);
  color: #d97706;
}

/* 确保树节点标签为flex布局 */
.bridge-tree-floating-panel .tree-node-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  user-select: none;
}

/* 无数据提示样式 */
.bridge-tree-floating-panel .no-data-tip {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
  margin: 10px;
  border: 1px dashed #d1d5db;
}

</style>
